import { clients, HttpError } from 'lib/https';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { ConfigurationSettings } from '/models/configuration-settings';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { getAccountWithAccessToken } from 'features/profile/services/accounts-service';
import { UserMembership } from 'types/user-type';

export interface SignInResponse {
  access_token?: string;
  refresh_token?: string;
  enable_mfa?: boolean;
  mfaId?: string;
  mfaType?: number;
  token_type?: string;
  expires_in?: number;
  scope?: string | null;
  id_token?: string | null;
}

interface AccountActivationData {
  firstName: string;
  lastName: string;
  password: string;
  code: string;
}

interface AccountActivationPayload extends AccountActivationData {
  ProjectKey: string;
  preventPostEvent: boolean;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captcha_code?: string;
  termsAndConditionsAccepted?: boolean;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
  provider: string;
};

export type OidcSigninPayload = {
  grantType: 'authorization_code';
  code: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export type CompleteSigninResponse = {
  accessToken: string;
  refreshToken: string;
  organizationId: string | null;
};

export type ISignupPayload = {
  email: string;
  firstName: string;
  lastName: string;
  captchaCode: string;
  terms: boolean;
};

export interface ISignupResponse {
  itemId: string | null;
  errors: unknown | null;
  isSuccess: boolean;
}

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
  provider: string;
}

export interface SigninByOidcPayload {
  grantType: 'authorization_code';
  code: string;
}

export const signin = async <
  T extends 'password' | 'social' | 'mfa_code' | 'authorization_code' = 'password'
>(
  payload: PasswordSigninPayload | MFASigninPayload | SigninBySSOPayload | SigninByOidcPayload,
  config: ConfigurationSettings
): Promise<T extends 'password' ? SignInResponse : MFASigninResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.login}`;

  // sign in flow
  if (payload.grantType === 'password') {    
    const requestBody: any = {
      username: payload.username,
      password: payload.password,
    };
    
    if (payload.captcha_code) {
      requestBody.captcha_code = payload.captcha_code;
    }

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (result?.error === 'mfa_enabled' || result?.mfa_required) {
      throw new HttpError(response.status, result);
    }

    if (!response.ok) {
      trackEvent(EVENTS.ERROR(result, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, result);
    }
    
    // Transform new response format to match existing format
    // The new API returns: { token_type, expires_in, scope, id_token }
    // We need to map id_token to both access_token and refresh_token for compatibility
    return {
      access_token: result.id_token || '',
      refresh_token: result.id_token || '',
      enable_mfa: result.enable_mfa || result.mfa_required || false,
      mfaId: result.mfaId || result.mfa_id || '',
      mfaType: result.mfaType ?? result.mfa_type ?? 0,
      token_type: result.token_type,
      expires_in: result.expires_in,
      scope: result.scope,
      id_token: result.id_token,
    } as any;
  } else if (payload.grantType === 'social') {
    const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || process.env.REACT_APP_PUBLIC_OIDC_CLIENT_ID || '';
    /* const signinBySSOData = new URLSearchParams();
    signinBySSOData.append('client_id', clientId);
    signinBySSOData.append('code', payload.code);
    signinBySSOData.append('state', payload.state); */

    const signinBySSOData = {
      client_id: clientId,
      code: payload.code,
      state: payload.state,
      provider: payload.provider
    };
    

    const response = await fetch(`${baseUrl}${config.paths.authentication.socialLogin}`, {
      method: 'POST',
      body: JSON.stringify(signinBySSOData),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'authorization_code') {
    const oidcData = new URLSearchParams();
    oidcData.append('grant_type', 'authorization_code');
    oidcData.append('code', payload.code);

    const response = await fetch(url, {
      method: 'POST',
      body: oidcData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else {
    // MFA OTP Verification flow
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        mfa_type: payload.mfaType,
        mfa_code: payload.code || '',
        mfa_id: payload.mfaId,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  }
};

export const signout = async (
  config: ConfigurationSettings
): Promise<{
  isSuccess: true;
}> => {
  try {
    localStorage.removeItem('auth-storage');
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.authentication.logout}`;
    // The httpOnly rt_ cookie (sent with credentials: 'include') names the session
    // to revoke, as for refresh. The body used to carry the id_token copy from the
    // store; an empty value keeps the request shape the password flow already sends.
    return await clients.post(url, config.blocksKey, JSON.stringify({ refreshToken: '' }));
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

let refreshInFlight: Promise<any> | null = null;

const REFRESH_LOCK_NAME = 'klax-auth-refresh';

// Tabs keep their own in-memory token, so each refreshes through the shared rt_
// cookie. A browser restoring several tabs would post the same cookie at once; if
// IAM rotates it, the later refresh could be refused. The Web Locks API queues
// refreshes across same-origin tabs, so the next one sends the rotated cookie.
const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks?.request) {
    return task();
  }
  let taskStarted = false;
  let gaveUpWaiting = false;
  // Waiting in the lock queue is bounded too: a holder tab whose JavaScript is
  // suspended mid-refresh must not hang this tab. The signal ends the wait in the
  // LockManager; the race covers one that ignores it. A lock granted after we gave
  // up is released at once without refreshing, so the refresh never runs twice.
  const waitLimit = new AbortController();
  let waitTimer: ReturnType<typeof setTimeout> | undefined;
  const waitTimedOut = new Promise<never>((_resolve, reject) => {
    waitTimer = setTimeout(() => {
      gaveUpWaiting = true;
      waitLimit.abort();
      reject(new DOMException('Waiting for the refresh lock timed out', 'TimeoutError'));
    }, REFRESH_TIMEOUT_MS);
  });
  const lockRequest = locks.request(REFRESH_LOCK_NAME, { signal: waitLimit.signal }, () => {
    if (gaveUpWaiting) return undefined;
    taskStarted = true;
    clearTimeout(waitTimer);
    // The lock is held until the task's promise settles (bounded by REFRESH_TIMEOUT_MS).
    return task();
  });
  // A rejection after the race has settled (the abort, say) is expected: not unhandled.
  lockRequest.catch(() => undefined);
  try {
    return (await Promise.race([lockRequest, waitTimedOut])) as T;
  } catch (error) {
    if (taskStarted) {
      throw error;
    }
    // The lock is unavailable (e.g. SecurityError in a sandboxed or opaque origin)
    // or the wait timed out: refresh without it rather than fail or hang.
    return task();
  } finally {
    clearTimeout(waitTimer);
  }
};

// A hung refresh must not hold refreshInFlight, the boot gate or the cross-tab lock.
const REFRESH_TIMEOUT_MS = 15000;

export const getRefreshToken = (config: ConfigurationSettings) => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = withRefreshLock(async () => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    // The refresh grant lives on its own JSON-only endpoint; the rt_ cookie is
    // the primary credential, so an empty body token is still refreshable.
    const url = `${baseUrl}${config.paths.authentication.refreshToken}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        method: 'POST',
        // Empty body token matches the https.ts interceptor: the rt_ cookie is
        // the credential, and a stale store value must not override it.
        body: JSON.stringify({ refresh_token: '' }),
        headers: {
          'Content-Type': 'application/json',
          'x-blocks-key': config.blocksKey,
        },
        credentials: 'include',
        signal: controller.signal,
      });

      if (!response.ok) {
        // An empty or HTML error body must still count as the HTTP status it is.
        const errorBody = await response.json().catch(() => ({}));
        trackEvent(
          EVENTS.ERROR(errorBody, {
            endpoint: config.paths.authentication.refreshToken,
          })
        );
        throw new HttpError(response.status, errorBody);
      }

      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const accountActivation = async (
  data: {
    firstName: string;
    lastName: string;
    password: string;
    code: string;
    captchaCode: string;
  },
  config: ConfigurationSettings
) => {
  const payload: AccountActivationPayload = {
    ...data,
    ProjectKey: config.blocksKey,
    preventPostEvent: true
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.activateAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const forgotPassword = async (
  data: {
    email: string;
    captchaCode?: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    mailPurpose: 'RecoverAccount',
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.recoverAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resetPassword = async (
  data: { code: string; password: string; captchaCode: string },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resetPassword}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resendActivation = async (
  data: {
    userId: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    projectKey: config.blocksKey,
    ...data,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resendActivation}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const logoutAll = async (config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.logoutAll}`;
  return clients.post(url, config.blocksKey, '');
};

export const switchOrganization = async (
  orgId: string,
  config: ConfigurationSettings,
  accessToken?: string
): Promise<MFASigninResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.switchOrganization}`;
  const token = accessToken ?? useAuthStore.getState().accessToken;

  return clients.post(
    url,
    config.blocksKey,
    JSON.stringify({ organization_id: orgId }),
    token ? { Authorization: `bearer ${token}` } : undefined
  );
};

const resolveOrganizationId = (memberships?: UserMembership[]) => {
  if (!memberships?.length) {
    return null;
  }

  return memberships.find((membership) => membership.organizationId !== 'default')?.organizationId
    ?? memberships[0]?.organizationId
    ?? null;
};

export const completeSigninWithOrganization = async (
  auth: Pick<MFASigninResponse, 'access_token' | 'refresh_token'>,
  config: ConfigurationSettings
): Promise<CompleteSigninResponse> => {
  const account = await getAccountWithAccessToken(config, auth.access_token);
  const organizationId = resolveOrganizationId(account.memberships);

  if (!organizationId) {
    return {
      accessToken: auth.access_token,
      refreshToken: auth.refresh_token,
      organizationId: null,
    };
  }

  const switchedAuth = await switchOrganization(organizationId, config, auth.access_token);

  return {
    accessToken: switchedAuth.access_token,
    refreshToken: switchedAuth.refresh_token ?? auth.refresh_token,
    organizationId,
  };
};

export const signup = async (
  payload: ISignupPayload,
  config: ConfigurationSettings
): Promise<ISignupResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.identifier.signup}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': config.blocksKey,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

/**
 * Generic function to refresh access token using refresh token
 * Can be called from anywhere in the application
 * @param config - Configuration settings containing API endpoints and blocks key
 * @returns Promise with new access token and refresh token
 * @throws HttpError if refresh token is invalid or expired
 */
type SessionSnapshot = { selectedOrgId: string | null };

const snapshotSession = (): SessionSnapshot => ({
  selectedOrgId: useAuthStore.getState().selectedOrgId,
});

/**
 * A refresh or switch that resolves late must not put a token back into a store
 * that has signed out (another tab logged out, say) or moved to another session.
 */
const isStillSameSession = (startedFor: SessionSnapshot) => {
  const { isAuthenticated, selectedOrgId } = useAuthStore.getState();
  return isAuthenticated && selectedOrgId === startedFor.selectedOrgId;
};

export const refreshAccessToken = async (config: ConfigurationSettings) => {
  const authStore = useAuthStore.getState();
  const startedFor = snapshotSession();

  try {
    const refreshTokenRes = await getRefreshToken(config);

    if (refreshTokenRes.error) {
      throw new HttpError(401, refreshTokenRes);
    }

    if (!isStillSameSession(startedFor)) {
      return refreshTokenRes;
    }

    // Cookie-mode responses may renew the session without body tokens; only
    // touch the store when tokens are actually present.
    if (refreshTokenRes.access_token) {
      authStore.setAccessToken(refreshTokenRes.access_token);
    }
    if (refreshTokenRes.refresh_token) {
      authStore.setRefreshToken(refreshTokenRes.refresh_token);
    }

    return refreshTokenRes;
  } catch (error) {
    // If it's already an HttpError, re-throw it
    if (error instanceof HttpError) {
      throw error;
    }
    
    // For other errors, wrap them in an HttpError
    console.error('Failed to refresh access token:', error);
    throw new HttpError(500, { error: 'Failed to refresh access token' });
  }
};

let sessionRestoreInFlight: Promise<void> | null = null;
let organizationCheckedThisPage = false;

// IAM answers these when the rt_ cookie is missing, expired or revoked.
const SESSION_REJECTED_STATUSES = new Set([400, 401, 403]);

// Hard limit on the org safety net: after it, the refreshed session is kept.
export const ORG_RESTORE_TIMEOUT_MS = 5000;

/**
 * Reads the Blocks JWT `org_id` claim (the claim the Flutter app reads too). It is
 * only used to decide whether to switch org, never trusted for access. Returns null
 * for an opaque token, a missing claim or no token (cookie mode).
 */
export const readTokenOrganizationId = (token?: string | null): string | null => {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replaceAll('-', '+').replaceAll('_', '/')));
    return typeof claims?.org_id === 'string' && claims.org_id ? claims.org_id : null;
  } catch {
    return null;
  }
};

/**
 * switch-org for the boot restore only. A raw fetch on purpose: through
 * clients.request it would wait on the restore gate it is part of, and a 401 would
 * be replayed by the https.ts interceptor into that same gate (a deadlock). Any
 * non-2xx simply means "switch failed".
 */
const switchOrganizationForRestore = async (
  config: ConfigurationSettings,
  organizationId: string,
  accessToken: string,
  signal: AbortSignal
): Promise<MFASigninResponse> => {
  const url = `https://${config.paths.baseUrl}${config.paths.authentication.switchOrganization}`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ organization_id: organizationId }),
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': config.blocksKey,
      Authorization: `bearer ${accessToken}`,
    },
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new Error(`switch-org failed with status ${response.status}`);
  }
  return response.json();
};

/**
 * Every sign-in switches to the user's organization (completeSigninWithOrganization)
 * and the org switcher does the same, but the boot refresh goes through the rt_ cookie.
 * If its token names another org than the persisted `selectedOrgId`, switch back once,
 * before the waiters get the token. If the org can't be read, skip it rather than
 * switch blindly. A failed or slow switch (ORG_RESTORE_TIMEOUT_MS) keeps the
 * refreshed session.
 */
const restoreSelectedOrganization = async (
  config: ConfigurationSettings,
  startedFor: SessionSnapshot,
  freshAccessToken?: string
) => {
  if (!isStillSameSession(startedFor)) return;
  const { selectedOrgId } = useAuthStore.getState();
  const tokenOrgId = readTokenOrganizationId(freshAccessToken);
  if (
    organizationCheckedThisPage ||
    !selectedOrgId ||
    !tokenOrgId ||
    !freshAccessToken ||
    tokenOrgId === selectedOrgId
  ) {
    return;
  }
  // At most one switch per page load, whatever its outcome: never loop.
  organizationCheckedThisPage = true;

  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve();
    }, ORG_RESTORE_TIMEOUT_MS);
  });
  const switching = switchOrganizationForRestore(
    config,
    selectedOrgId,
    freshAccessToken,
    controller.signal
  )
    .then((switched) => {
      if (controller.signal.aborted || !isStillSameSession(startedFor)) return;
      if (switched?.access_token) {
        useAuthStore.getState().setAccessToken(switched.access_token);
      }
      if (switched?.refresh_token) {
        useAuthStore.getState().setRefreshToken(switched.refresh_token);
      }
    })
    .catch(() => {
      console.error('Could not restore the selected organization after reload');
    });

  try {
    await Promise.race([switching, timedOut]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Tokens are kept in memory only, so after a reload (or in a new tab) the store
 * just knows the user was signed in. Before the first authenticated request, one
 * refresh through the rt_ cookie brings a fresh access token back, switched to the
 * persisted organization if needed. Every caller that arrives meanwhile waits for
 * the same restore (and the same `refreshInFlight` underneath). Signed-out users
 * and IdP sessions never wait: needsSessionRestore is false for them.
 *
 * The restore itself only uses raw fetches (refresh, switch-org), never
 * clients.request, so nothing it waits on can be queued behind this gate.
 *
 * If IAM refuses the cookie, the session is over: sign out and go to /login.
 * Network or server trouble keeps the session; the 401 handler in https.ts still
 * refreshes or signs out later if it has to.
 */
export const waitForSessionRestore = (): Promise<void> => {
  // In-flight first: callers that arrive while the restore runs always wait for it,
  // whatever the flag says. Only the restore clears the flag, when it settles.
  if (sessionRestoreInFlight) {
    return sessionRestoreInFlight;
  }
  if (!useAuthStore.getState().needsSessionRestore) {
    return Promise.resolve();
  }

  const config = useConfigStore.getState().configurationSettings;
  if (!config) {
    return Promise.resolve();
  }

  const startedFor = snapshotSession();
  sessionRestoreInFlight = refreshAccessToken(config)
    .then((refreshed) => restoreSelectedOrganization(config, startedFor, refreshed?.access_token))
    .then(() => {
      // Cookie-mode responses may carry no body token; the restore is still done.
      useAuthStore.getState().setSessionRestored();
    })
    .catch((error: unknown) => {
      if (error instanceof HttpError && SESSION_REJECTED_STATUSES.has(error.status)) {
        useAuthStore.getState().logout();
        globalThis.location.href = '/login';
        throw error;
      }
      useAuthStore.getState().setSessionRestored();
    })
    .finally(() => {
      sessionRestoreInFlight = null;
    });

  return sessionRestoreInFlight;
};
